import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useGlobalPaywall } from '@/contexts/PaywallContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { supabase } from '@/integrations/supabase/client';
import {
  FEATURES,
  getFeatureAccess,
  type FeatureAccess,
  type FeatureId,
  type FeatureRule,
} from '@/lib/features';

interface FeatureOverride {
  feature_id: string;
  access: FeatureAccess;
  reason: string | null;
  expires_at: string | null;
}

export interface UseFeatureResult {
  access: FeatureAccess;
  allowed: boolean;
  blocked: boolean;
  upsell: boolean;
  /**
   * Abre o paywall quando a feature exige PRO. `auto: true` marca abertura por
   * EFEITO de tela — ela é silenciada depois que o usuário dispensa uma vez
   * (D11). Sem argumento é gesto do usuário, e abre sempre.
   */
  require: (options?: { auto?: boolean }) => boolean;
  rule: FeatureRule;
}

const CACHE_PREFIX = 'orcamigo_feature_overrides';

function cacheKey(userId: string): string {
  return `${CACHE_PREFIX}:${userId}`;
}

function readCachedOverrides(userId: string): FeatureOverride[] {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    return raw ? (JSON.parse(raw) as FeatureOverride[]) : [];
  } catch {
    return [];
  }
}

function writeCachedOverrides(userId: string, overrides: FeatureOverride[]): void {
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify(overrides));
  } catch {
    // A cache failure must never remove an entitlement from the current session.
  }
}

function isActiveOverride(override: FeatureOverride, now = Date.now()): boolean {
  return !override.expires_at || new Date(override.expires_at).getTime() > now;
}

export function useFeature(id: FeatureId): UseFeatureResult {
  const { user } = useAuth();
  const { planType } = useSubscription();
  const [, openPaywall] = useGlobalPaywall();

  const { data: overrides = [] } = useQuery({
    queryKey: ['feature-overrides', user?.id],
    enabled: Boolean(user?.id),
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => (user?.id ? readCachedOverrides(user.id) : []),
    staleTime: 5 * 60 * 1000,
    // `initialData` (do localStorage) mostra o valor na hora, sem flash — mas o
    // react-query o trata como FRESH por `staleTime`, então um override novo
    // (grant de suporte, upgrade de plano) não aparecia por até 5 min numa sessão
    // nova, e os refetch de foco/reconexão estão desligados globalmente (App.tsx).
    // `refetchOnMount: 'always'` revalida a entitlement em todo mount, mantendo o
    // cache instantâneo + offline-first.
    refetchOnMount: 'always',
    networkMode: 'offlineFirst',
    retry: 1,
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('feature_overrides')
        .select('feature_id, access, reason, expires_at')
        .eq('user_id', user.id);

      if (error) throw error;

      const next = (data ?? []) as FeatureOverride[];
      writeCachedOverrides(user.id, next);
      return next;
    },
  });

  const rule = FEATURES[id] as FeatureRule;
  const override = overrides.find(
    (candidate) => candidate.feature_id === id && isActiveOverride(candidate),
  );
  const access = override?.access ?? getFeatureAccess(id, planType);

  // `options.auto` marca a abertura disparada por EFEITO de tela (nunca por um
  // gesto): o provider a silencia depois da primeira dispensa, que é o que
  // impede o laço do D11. Chamar `require()` sem argumento continua sendo "o
  // usuário pediu" — e aí o paywall abre sempre.
  const require = useCallback(
    (options: { auto?: boolean } = {}) => {
      if (access === 'allowed') return true;
      if (access === 'upsell') {
        openPaywall({
          reason: rule.upsellReason ?? 'feature',
          customMessage: override?.reason ?? undefined,
          auto: options.auto,
        });
      }
      return false;
    },
    [access, openPaywall, override?.reason, rule.upsellReason],
  );

  return {
    access,
    allowed: access === 'allowed',
    blocked: access === 'blocked',
    upsell: access === 'upsell',
    require,
    rule,
  };
}
